import { localPrice, PagingParameters } from "@/api/dtos";
import { FullShopOrder, useGetOrders } from "@/api/order";
import { DataTable } from "@/components/ui/datatable";
import { DatatablePagination } from "@/components/ui/datatable-pagination";
import i18n from "@/i18n";
import { ColumnDef, Row, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { OrderEditDrawer } from "./OrderEditDrawer";
import { PurchaseStatusBadge } from "./PurchaseStatusBadge";
import { generateSortableHeader } from "@/components/ui/shared";

const baseColumns: ColumnDef<FullShopOrder>[] = [
  {
    id: "instanceOrderNumber",
    size: 1,
    header: generateSortableHeader("N°"),
    cell: ({ row }) => (
      <>
        <span className="font-medium inline-block w-fit text-nowrap">
          N{row.original.orderNumber.toString().padStart(5, "0")}
        </span>
      </>
    ),
  },
  {
    id: "purchaseStatus",
    size: 1,
    header: generateSortableHeader("Statut"),
    cell: ({ row }) => (
      <div className="px-4">
        <PurchaseStatusBadge status={row.original.purchaseStatus} />
      </div>
    ),
  },
  {
    id: "priceAmount",
    size: 1,
    header: generateSortableHeader("Montant total"),
    cell: ({ row }) => (
      <>
        <span>{localPrice(i18n.language, row.original.priceAmount)} €</span>
      </>
    ),
  },
];

const emailField: ColumnDef<FullShopOrder> = {
  id: "customerEmail",
  size: 10000,
  header: generateSortableHeader("Client"),
  cell: ({ row }) => (
    <>
      <span>{row.original.customer.email.toString()}</span>
    </>
  ),
};

export const OrderList = ({
  search,
  customerEmail,
}: {
  search?: string;
  customerEmail?: string;
}) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(() => {
    if (customerEmail) return baseColumns;
    return [baseColumns[0], emailField, baseColumns[1], baseColumns[2]];
  }, [customerEmail]);

  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  const [orderSelected, setOrderSelected] = useState<
    FullShopOrder | undefined
  >();

  const { data } = useGetOrders(getPagination, sorting, search, customerEmail);

  const onSelectRow = (row: Row<FullShopOrder>) => {
    setOrderSelected(undefined);
    setOrderSelected(row.original);
    setSheetOpen(true);
  };

  return (
    <>
      {orderSelected && (
        <OrderEditDrawer
          open={sheetOpen}
          setOpen={setSheetOpen}
          order={orderSelected}
        />
      )}
      <DataTable
        onRowClick={onSelectRow}
        columns={columns}
        data={data?.data ?? []}
        options={{
          state: { sorting: sorting },
          onSortingChange: setSorting,
        }}
      ></DataTable>
      <DatatablePagination
        pagination={getPagination}
        setPage={setPagination}
        totalSize={data?.totalSize ?? 0}
      />
    </>
  );
};
